import { useRef, useState } from 'react'

import { OperationVariables, TypedDocumentNode } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import moment, { Moment } from 'moment'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { dateUIToBackend } from 'modules/core/lib/dates'

import ScheduleActions from 'modules/project_management/components/Schedule/Actions'
import ScheduleFooter from 'modules/project_management/components/Schedule/Footer'
import { FooterElement } from 'modules/project_management/components/Schedule/Footer/index'
import ScheduleHeader from 'modules/project_management/components/Schedule/Header'

import ScheduleBody, { ScheduleParseDataType } from './Body'
import { ScheduleRecordValuesType } from './Body/ScheduleItem/types'
import ScheduleEmptyState from './ScheduleEmptyState'
import { ScheduleSidepanel } from './Sidepanel'

export type DateRange = [Date, Date]
interface ScheduleProps<TQuery, TVariables> {
  document: TypedDocumentNode<TQuery, TVariables>
  documentVariables: TVariables
  dataSource: (data: NonNullable<TQuery>) => {
    edges: Edge[]
    pageInfo: { hasNextPage: boolean; endCursor: string }
  }
  footerLegend?: FooterElement[]
  parseData: (data: TQuery | undefined, startDate: Moment, endDate: Moment) => ScheduleParseDataType
  showBetaTag?: boolean
  isMyProjectsView?: boolean
}

interface Edge {
  cursor: string
  node: unknown
}

const HEADER_AND_ACTIONS_HEIGHT = 226

const Schedule = <TQuery, TVariables extends OperationVariables>({
  document,
  documentVariables,
  dataSource,
  footerLegend,
  parseData,
  showBetaTag = false,
  isMyProjectsView = false,
}: ScheduleProps<TQuery, TVariables>) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [range, setRange] = useState<DateRange>([
    moment().startOf('month').toDate(),
    moment().endOf('month').toDate(),
  ])
  const [filters, setFilters] = useState<Record<string, number[]>>({})
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const vars = {
    ...documentVariables,
    startDate: range[0],
    endDate: range[1],
    ...filters,
    employeeName: debouncedSearch,
  }
  const onChangeFilter = (filterValue: Record<string, number[]>) => {
    setFilters((prev) => ({ ...prev, ...filterValue }))
  }

  const {
    loading,
    data,
    controls: { fetchMore, hasMore },
  } = usePaginatedQuery(document, {
    variables: { ...vars },
    extractConnection: dataSource,
  })

  const parsedData = parseData(data, moment(range[0]), moment(range[1]))
  const [openAssignHoursSidepanel, setOpenAssignHoursSidepanel] = useState(false)
  const [sidePanelData, setSidePanelData] = useState<ScheduleRecordValuesType | null>(null)
  const [preselectedEmployee, setPreselectedEmployee] =
    useState<EmployeeForSelectFragmentNoAccess | null>(null)

  const closeAssignHoursSidepanel = () => {
    setOpenAssignHoursSidepanel(false)
  }

  const handleStartDateChange = (date: string) => {
    if (!!date) {
      const dateFormatted = moment(dateUIToBackend(date))
      const startOfMonth = dateFormatted.startOf('month').toDate()
      const endOfMonth = dateFormatted.endOf('month').toDate()
      setRange([startOfMonth, endOfMonth])
    }
  }

  const openSidepanel = (
    recordData?: ScheduleRecordValuesType | null,
    employee?: EmployeeForSelectFragmentNoAccess | null
  ) => {
    setSidePanelData(recordData || null)
    setPreselectedEmployee(employee || null)
    setOpenAssignHoursSidepanel(true)
  }

  const isEmpty = parsedData.totalCount < 0
  return (
    <>
      {isEmpty ? (
        <ScheduleEmptyState />
      ) : (
        <>
          <Box position="sticky" style={{ top: '0', zIndex: '4', backgroundColor: '#fff' }}>
            <ScheduleActions
              range={range}
              setRange={setRange}
              openSidepanel={openSidepanel}
              showBetaTag={showBetaTag}
              onChangeFilter={onChangeFilter}
              onSearch={onSearch}
              search={search}
              isMyProjectsView={isMyProjectsView}
            />
          </Box>
          <Box
            overflowY="auto"
            ref={scrollRef}
            style={{ maxHeight: `calc(100vh - ${HEADER_AND_ACTIONS_HEIGHT}px)` }}
          >
            <ScheduleHeader range={range} />
            <ScheduleBody
              loading={loading}
              parsedData={parsedData}
              fetchMore={fetchMore}
              hasMore={hasMore}
              range={range}
              scrollContainerRef={scrollRef}
              openSidepanel={openSidepanel}
              isMyProjectsView={isMyProjectsView}
            />
          </Box>
          {!isMyProjectsView && openAssignHoursSidepanel && (
            <ScheduleSidepanel
              open={openAssignHoursSidepanel}
              onClose={closeAssignHoursSidepanel}
              onDateChange={handleStartDateChange}
              id={sidePanelData?.id}
              dailyMinutes={sidePanelData?.dailyMinutes}
              startDate={sidePanelData?.startDate}
              endDate={sidePanelData?.endDate}
              projectWorkerIds={
                sidePanelData?.projectWorkerId ? [sidePanelData?.projectWorkerId] : []
              }
              project={sidePanelData?.project}
              subproject={sidePanelData?.subproject}
              employee={sidePanelData?.employee || preselectedEmployee || null}
            />
          )}
          {footerLegend && <ScheduleFooter footerLegend={footerLegend} />}
        </>
      )}
    </>
  )
}

export default Schedule
